import './JobCard.css';
import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LaptopIcon from '@mui/icons-material/Laptop';
import BusinessIcon from '@mui/icons-material/Business';
import { NavLink } from 'react-router-dom';
import { useJobContext } from '../../JobContext';
import { formatAppDate } from '../../utils/dateFormat';

interface ButtonConfig {
  backgroundColor: string;
  backgroundColorOnHover: string;
  text: string;
  url?: string;
}

export enum ApplicationStatus {
  INCOMPLETE = 0,
  PENDING_REVIEW = 1,
  PENDING_DECISION = 2,
  SHORTLISTED = 3,
  ACCEPTED = 4,
  REJECTED = 5,
}

interface JobStatusCardProps {
  title: string;
  city: string;
  province: string;
  country: string;
  currentlyAvailable: boolean;
  id: number;
  applicationStatus: ApplicationStatus;
  submittedAt?: string | null;
  remote?: boolean;
  jobType?: string | number;
}

interface StatusButtonProps {
  status: ApplicationStatus;
  jobId: number;
}

const getButtonConfig = (
  status: ApplicationStatus,
  jobId: number,
): ButtonConfig => {
  switch (status) {
    case ApplicationStatus.INCOMPLETE:
      return {
        backgroundColor: 'var(--ttg-black)',
        backgroundColorOnHover: 'var(--ttg-black)',
        text: 'Continue To Submission',
        url: `/assessments/${jobId}`,
      };
    case ApplicationStatus.PENDING_REVIEW:
      return {
        backgroundColor: 'var(--status-under-review)',
        backgroundColorOnHover: 'var(--status-under-review)',
        text: 'Pending Review',
      };
    case ApplicationStatus.PENDING_DECISION:
      return {
        backgroundColor: 'var(--links)',
        backgroundColorOnHover: 'var(--links)',
        text: 'Pending Decision',
      };
    case ApplicationStatus.ACCEPTED:
      return {
        backgroundColor: 'var(--ttg-green)',
        backgroundColorOnHover: 'var(--ttg-green)',
        text: 'Accepted',
      };
    case ApplicationStatus.REJECTED:
      return {
        backgroundColor: 'var(--status-not-selected)',
        backgroundColorOnHover: 'var(--status-not-selected)',
        text: 'Rejected',
      };
    case ApplicationStatus.SHORTLISTED:
      return {
        backgroundColor: 'var(--status-shortlisted)',
        backgroundColorOnHover: 'var(--status-shortlisted)',
        text: 'Shortlisted',
      };
    default:
      return {
        backgroundColor: 'var(--ttg-black)',
        backgroundColorOnHover: 'var(--ttg-black)',
        text: 'N/A',
      };
  }
};

const StatusButton: React.FC<StatusButtonProps> = ({ status, jobId }) => {
  const buttonConfig = getButtonConfig(status, jobId);

  // Desktop button styles (md and up - 900px+)
  const desktopSx = {
    backgroundColor: buttonConfig.backgroundColor,
    '&:hover': {
      backgroundColor: buttonConfig.backgroundColorOnHover,
    },
    marginTop: { md: '10px', lg: '20px' },
    padding: { md: '8px 16px', lg: '10px 20px' },
    fontSize: { md: '0.75rem', lg: '0.875rem' },
    cursor: buttonConfig.url ? 'pointer' : 'auto',
    width: { md: '180px', lg: '240px' },
    minWidth: { md: '180px', lg: '240px' },
    whiteSpace: 'nowrap',
  };

  // Tablet button styles (sm to md - 600px to 900px)
  const tabletSx = {
    backgroundColor: buttonConfig.backgroundColor,
    '&:hover': {
      backgroundColor: buttonConfig.backgroundColorOnHover,
    },
    marginTop: '8px',
    padding: '6px 12px',
    fontSize: '0.7rem',
    cursor: buttonConfig.url ? 'pointer' : 'auto',
    width: '170px',
    minWidth: '170px',
    whiteSpace: 'nowrap',
  };

  // Mobile button styles (xs - below 600px)
  const mobileSx = {
    backgroundColor: buttonConfig.backgroundColor,
    '&:hover': {
      backgroundColor: buttonConfig.backgroundColorOnHover,
    },
    marginTop: '5px',
    padding: '6px 12px',
    fontSize: '0.65rem',
    cursor: buttonConfig.url ? 'pointer' : 'auto',
    width: '160px',
    minWidth: '160px',
    whiteSpace: 'nowrap',
  };

  if (buttonConfig.url) {
    return (
      <>
        {/* Desktop Button (900px+) */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <NavLink to={buttonConfig.url}>
            <Button
              className="viewDetailsButton"
              variant="contained"
              sx={desktopSx}
            >
              {buttonConfig.text}
            </Button>
          </NavLink>
        </Box>
        {/* Tablet Button (600px - 900px) */}
        <Box sx={{ display: { xs: 'none', sm: 'block', md: 'none' } }}>
          <NavLink to={buttonConfig.url}>
            <Button
              className="viewDetailsButton"
              variant="contained"
              sx={tabletSx}
            >
              {buttonConfig.text}
            </Button>
          </NavLink>
        </Box>
        {/* Mobile Button (below 600px) */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <NavLink to={buttonConfig.url}>
            <Button
              className="viewDetailsButton"
              variant="contained"
              sx={mobileSx}
            >
              {buttonConfig.text}
            </Button>
          </NavLink>
        </Box>
      </>
    );
  } else {
    return (
      <>
        {/* Desktop Button (900px+) */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Button
            className="viewDetailsButton"
            variant="contained"
            sx={desktopSx}
          >
            {buttonConfig.text}
          </Button>
        </Box>
        {/* Tablet Button (600px - 900px) */}
        <Box sx={{ display: { xs: 'none', sm: 'block', md: 'none' } }}>
          <Button
            className="viewDetailsButton"
            variant="contained"
            sx={tabletSx}
          >
            {buttonConfig.text}
          </Button>
        </Box>
        {/* Mobile Button (below 600px) */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <Button
            className="viewDetailsButton"
            variant="contained"
            sx={mobileSx}
          >
            {buttonConfig.text}
          </Button>
        </Box>
      </>
    );
  }
};

const JobStatusCard: React.FC<JobStatusCardProps> = ({
  title,
  city,
  province,
  country,
  currentlyAvailable,
  id,
  applicationStatus,
  submittedAt,
  remote,
  jobType,
}) => {
  const formatSubmissionDate = (dateString: string | null | undefined) =>
    formatAppDate(dateString, 'Date not available');

  // Map numeric jobType (from backend) to label using JobContext
  const { jobTypeMapping } = useJobContext();
  const inverseJobType: { [key: string]: string } = Object.fromEntries(
    Object.entries(jobTypeMapping).map(([key, value]) => [String(value), key]),
  );

  const jobTypeLabel = (() => {
    if (!jobType && jobType !== 0) return undefined;
    const key = typeof jobType === 'number' ? String(jobType) : String(jobType).trim();
    return inverseJobType[key] || (typeof jobType === 'string' ? jobType : undefined);
  })();

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent className="jobCardContent">
        <Grid container spacing={1} alignItems="center">
          {/* Title Section */}
          <Grid item xs={12} md={8} container direction="row">
            <Grid item>
              <Typography
                variant="h5"
                sx={{
                  textAlign: 'left',
                  fontWeight: 'bold',
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.25rem', lg: '1.5rem' }
                }}
              >
                {title}
              </Typography>
            </Grid>
          </Grid>

          {/* Button Section - Desktop only (900px+) */}
          <Grid item xs={12} md={4} container justifyContent="flex-end" sx={{ display: { xs: 'none', md: 'flex' } }}>
            <StatusButton status={applicationStatus} jobId={id} />
          </Grid>

          {/* Metadata Section - Desktop with icons (900px+) */}
          <Grid
            item
            container
            xs={12}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-start' }}
          >
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <LocationOnIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
                <Typography
                  variant="body2"
                  color="var(--ttg-black)"
                  textAlign="left"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {`${city}, ${province}, ${country}`}
                </Typography>
              </Box>
            </Grid>

            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {remote ? (
                  <LaptopIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
                ) : (
                  <BusinessIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
                )}
                <Typography
                  variant="body2"
                  color="var(--ttg-black)"
                  textAlign="left"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {remote ? 'Remote' : 'On-site'}
                </Typography>
              </Box>
            </Grid>

            {jobType && (
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <AccessTimeIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
                  <Typography
                    variant="body2"
                    color="var(--ttg-black)"
                    textAlign="left"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {jobTypeLabel || jobType}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Metadata Section - Mobile/Tablet with separators (below 900px) */}
          <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' }, mb: 1 }}>
            <Typography
              variant="body2"
              color="var(--ttg-black)"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, textAlign: 'left' }}
            >
              {`${city}, ${province}, ${country}`} | {remote ? 'Remote' : 'On-site'}{jobTypeLabel ? ` | ${jobTypeLabel}` : ''}
            </Typography>
          </Grid>

          {/* Button Section - Mobile/Tablet only (below 900px) */}
          <Grid item xs={12} container justifyContent="flex-start" sx={{ display: { xs: 'flex', md: 'none' } }}>
            <StatusButton status={applicationStatus} jobId={id} />
          </Grid>

          {submittedAt && (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic', marginTop: '8px' }}
              >
                Submitted: {formatSubmissionDate(submittedAt)}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default JobStatusCard;
export { JobStatusCardProps };
